import React from "react";

import { IconMetrics } from "~/components/icon";
import { MetricQuery } from "~/models/metric-query";
import { isError } from "~/tools/is-error";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";

import type { BoundMetric } from "../../../common/openapi";
import { queryComponentBoundMetric } from "../../../queries/components/metrics";
import { useQuery } from "../../../queries/react-query";
import { DrawerTitle, ExplorationDrawer } from "../../react/drawer/ExplorationDrawer";
import { Content } from "../_content";
import { PinComponentButton } from "../_pin-component-button";

import type { BoundMetricsDrawerState } from "./state";

function selectModel({ boundQueries, alias, name, unit }: BoundMetric) {
	return RemoteData.success({
		metricQueries: boundQueries.map(MetricQuery),
		title: alias ?? name,
		unit,
	});
}

export interface BoundMetricsDrawerProps {
	when: undefined | BoundMetricsDrawerState;
}

export const BoundMetricsDrawer: React.FC<BoundMetricsDrawerProps> = ({ when }) => {
	const { error, data = RemoteData.loading() } = useQuery({
		enabled: when != null,
		select: selectModel,

		...queryComponentBoundMetric({
			componentIdOrIdentifier: when?.componentIdOrIdentifier ?? "",
			metricBindingIdentifier: when?.metricBindingIdentifier ?? "",
			timestamp: when?.timestamp,
		}),
	});

	return (
		<ExplorationDrawer
			headerActions={({ componentIdOrIdentifier, timestamp }) => {
				if (componentIdOrIdentifier == null || error != null) {
					return null;
				}

				const model = data.getOrElse(undefined);

				return (
					<PinComponentButton
						componentIdOrIdentifier={componentIdOrIdentifier}
						metricQueries={model?.metricQueries}
						timestamp={timestamp}
						title={model?.title}
						unit={model?.unit}
					/>
				);
			}}
			headerTitle={() => (
				<DrawerTitle
					icon={<IconMetrics height={20} width={20} />}
					title={data.transform(prop("title")).getOrElse(undefined)}
				/>
			)}
			when={when}
		>
			{({ timestamp, timeRange }) => (
				<Content
					model={isError(error) ? RemoteData.failure(error) : data}
					timeRange={timeRange}
					timestamp={timestamp}
				/>
			)}
		</ExplorationDrawer>
	);
};
